import type { ReactNode } from "react";

import { ChatTaskPanelBody } from "./ChatTaskPanelBody";
import { useChatTaskPanelContext, type ChatTaskPanelAnchor } from "./useChatTaskPanelContext";

export interface ChatTaskPanelProps {
  readonly anchor: ChatTaskPanelAnchor;
}

/**
 * The right sidebar's Task surface.
 *
 * Resolves the thread's CMMD anchor, then gates on a project id before mounting
 * the body — the board and workbench reads fire on mount, so they cannot start
 * before a project is known. The body is keyed by the resolved ids so switching
 * threads remounts it instead of leaking the previous thread's selection.
 */
export function ChatTaskPanel(props: ChatTaskPanelProps) {
  const context = useChatTaskPanelContext(props.anchor);

  if (context.error !== null) {
    return <PanelMessage>{context.error}</PanelMessage>;
  }
  if (context.loading) {
    return <PanelMessage>Loading task…</PanelMessage>;
  }
  if (context.projectId === null) {
    return <PanelMessage>This thread is not linked to a CMMD project or task.</PanelMessage>;
  }
  return (
    <ChatTaskPanelBody
      key={`${context.projectId}:${context.taskId ?? "none"}`}
      projectId={context.projectId}
      context={context}
    />
  );
}

function PanelMessage({ children }: { readonly children: ReactNode }) {
  return (
    <div className="flex h-full items-center justify-center px-6 text-center text-sm text-muted-foreground">
      {children}
    </div>
  );
}
